import React, { useMemo } from 'react';
import { AvTooltip } from '@fabric-ui/components';
import { flex } from '@fabric-ui/theme';
import { ellipsis, emptyObject, noop } from '@fabric-ui/utils';
import { Button, Chip, SxProps } from '@mui/material';
import { popupIcon } from './constants';

export interface SelectTriggerProps {
  ariaDescribedby?: 'filter-mode' | undefined;
  selectIcon?: React.ReactNode;
  placeholder?: string;
  transparent?: boolean;
  showSelection?: boolean;
  showPlaceholder?: boolean;
  disabled?: boolean;
  disabledTooltipText?: string | undefined;
  label: React.ReactNode | string;
  size: 'xSmall' | 'small' | 'medium';
  open: boolean;
  onClick: VoidFunction;
  processedValue?: any;
  tooltipValue?: string;
  displayValue?: string;
  hasValidValue?: boolean;
  chipCount?: number | undefined;
  showChipCount?: boolean;
  variant?: 'text' | 'outlined' | 'contained' | 'filter';
  type?: 'button' | 'reset' | 'submit';
  sx?: SxProps;
  ref?: React.RefObject<HTMLButtonElement | null>;
}

export const SelectTrigger = ({
  ariaDescribedby,
  selectIcon = popupIcon,
  placeholder,
  transparent,
  showSelection = true,
  showPlaceholder,
  disabled,
  disabledTooltipText,
  label,
  size = 'medium',
  open,
  onClick = noop,
  processedValue,
  tooltipValue,
  displayValue,
  hasValidValue,
  chipCount,
  showChipCount = true,
  variant = 'filter',
  type,
  sx = emptyObject,
  ref,
}: SelectTriggerProps) => {
  const width = useMemo(() => (open ? ref?.current?.getBoundingClientRect().width : undefined), [open]);
  const content = (
    <Button
      ref={ref}
      variant={variant}
      type={type}
      aria-describedby={ariaDescribedby}
      sx={{ maxWidth: 300, width, justifyContent: 'space-between', ...sx }}
      className={[
        open ? 'Mui-focusVisible' : '',
        hasValidValue ? 'active-filter' : '',
        showPlaceholder ? 'placeholder' : '',
        transparent ? 'transparent' : '',
      ]
        .filter(d => d)
        .join(' ')}
      onClick={onClick}
      size={size}
      disabled={disabled}>
      {showPlaceholder ? (
        <span style={ellipsis}>{placeholder}</span>
      ) : (
        (label || processedValue) && (
          <>
            <span style={{ ...ellipsis, ...(open ? { textOverflow: 'clip' } : {}), ...flex.row, gap: '6px' }}>
              {label}
              {label && displayValue ? ': ' : ''}
              {displayValue && <span style={ellipsis}>{displayValue}</span>}
              {showChipCount && chipCount !== undefined && chipCount > 0 && (
                <AvTooltip variant="message" title={tooltipValue}>
                  <Chip label={`+${chipCount}`} size="small" />
                </AvTooltip>
              )}
            </span>
            {showSelection ? selectIcon : ''}
          </>
        )
      )}
    </Button>
  );
  return disabled && disabledTooltipText ? (
    <AvTooltip title={disabledTooltipText}>
      <div>{content}</div>
    </AvTooltip>
  ) : (
    content
  );
};
